import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

import { useLandingPage } from '@entities/LandingPage'

import { LandingPageBuilder } from '@features/landing-page-builder'

import NotFoundComponent from '@widgets/not-found/NotFoundComponent'

import SEO from '@shared/components/SEO'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

function LandingSkeleton () {
  return (
    <Container>
      <Wrapper className={styles.wrapper}>
        <SkeletonTheme>
          <Skeleton height={190} style={{ borderRadius: 24, marginBottom: 24 }} />
          <Skeleton width="40%" height={28} style={{ marginBottom: 16 }} />
          <div className={styles.skeleton__row}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={180} style={{ borderRadius: 16 }} />
            ))}
          </div>
        </SkeletonTheme>
      </Wrapper>
    </Container>
  )
}

/*
  Renders a backend-configured landing page by its code (/landing/:code).

  The page is a thin shell on purpose: the backend decides which blocks exist,
  in what order and for which state, and the block mapper renders them. Adding a
  block type is a backend change plus a registry entry — not a new page.
*/
export default function LandingPage () {
  const { code } = useParams<{ code: string }>()

  const { landingPage, loading } = useLandingPage({ code })

  // First load, nothing cached yet.
  if (loading && !landingPage) {
    return <LandingSkeleton />
  }

  // No such landing, or it is no longer active.
  if (!landingPage) {
    return (
      <Container>
        <Wrapper className={styles.wrapper}>
          <NotFoundComponent />
        </Wrapper>
      </Container>
    )
  }

  const { name, blocks, actions, header, promo } = landingPage

  return (
    <Container>
      {name && <SEO title={name} description={name} />}

      <Wrapper className={styles.wrapper}>
        {/*
          header.showHeader === true means the landing wants its name rendered
          above the blocks; on event landings the hero is a block instead.
        */}
        {header?.showHeader && name && <h1 className={styles.title}>{name}</h1>}

        <LandingPageBuilder blocks={blocks} actions={actions} promo={promo} />
      </Wrapper>
    </Container>
  )
}
